import { type TableInformation } from '../../../config/flavourConfig';
import type {
    CatDTO,
    DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import type {
    AdultAccidentDTO,
    ChildAccidentDTO,
    YoungAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import type { TravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/tra';
import { toLongDateString } from '../../../utils/dateUtil';
import type {
    PrivateContentDTO,
    YouthContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';

export const getCoverableInformationCoverableDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case 'Hundeforsikring':
            return getDogInformationCoverableDetailsConfig(coverable);
        case 'Katteforsikring':
            return getCatInformationCoverableDetailsConfig(coverable);
        case 'Ulykkesforsikring':
        case 'Ulykkesforsikring til unge':
            return getAdultYoungAccidentInformationCoverableDetailsConfig(coverable);
        case 'Børneforsikring':
            return getChildAccidentInformationCoverableDetailsConfig(coverable);
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return getTravelInformationCoverableDetailsConfig(coverable);
        case 'Indboforsikring til unge':
            return getYouthContentInformationCoverableDetailsConfig(coverable);
        case 'Indboforsikring':
            return getPrivateContentInformationCoverableDetailsConfig(coverable);
        case 'Bilforsikring':
            return getPrivateCarInformationCoverableDetailsConfig(
                coverable as CarDTO,
                getHippoText
            );
        default:
            return undefined;
    }
};

const getDogInformationCoverableDetailsConfig = (dogCoverable: DogDTO): TableInformation => {
    return {
        title: 'Oplysinger om ' + (dogCoverable.name ?? 'forsikring'),
        rowsInformation: [
            {
                name: 'Race',
                fieldValue: dogCoverable.breed
            },
            {
                name: 'Hundens anvendelse',
                fieldValue: dogCoverable.dogUse
            },
            {
                name: 'Køn',
                fieldValue: dogCoverable.gender
            },
            {
                name: 'Født',
                fieldValue: dogCoverable.dateOfBirth
            },
            {
                name: 'Chip-nummer / ID-nummer',
                fieldValue: dogCoverable.chipIdNumber ?? 'Ikke oplyst'
            },
            {
                name: 'Tjenestehund-ID-nummer',
                fieldValue: dogCoverable.policeDogID ?? 'Ikke oplyst',
                hidden: dogCoverable.dogUse !== 'Tjenestehund'
            },
            {
                name: 'Medlemsskab af hundeklub',
                fieldValue: dogCoverable.dogClub
            }
        ]
    };
};

const getCatInformationCoverableDetailsConfig = (catCoverable: CatDTO): TableInformation => {
    return {
        title: 'Oplysinger om ' + (catCoverable.name ?? 'forsikring'),
        rowsInformation: [
            {
                name: 'Race',
                fieldValue: catCoverable.breed
            },
            {
                name: 'Køn',
                fieldValue: catCoverable.gender
            },
            {
                name: 'Født',
                fieldValue: catCoverable.dateOfBirth
            },
            {
                name: 'Chip-nummer / ID-nummer',
                fieldValue: catCoverable.chipIdNumber ?? 'Ikke oplyst'
            },
            {
                name: 'Udekat',
                fieldValue: catCoverable.outdoorCat ? 'Ja' : 'Nej'
            }
        ]
    };
};

const getAdultYoungAccidentInformationCoverableDetailsConfig = (
    accidentCoverable: AdultAccidentDTO | YoungAccidentDTO
): TableInformation => {
    return {
        title: 'Oplysninger om dig',
        rowsInformation: [
            {
                name: 'Fulde navn',
                fieldValue: accidentCoverable.fullName
            },
            {
                name: 'CPR-nummer',
                fieldValue: accidentCoverable.cprNumber
            }
        ]
    };
};

const getChildAccidentInformationCoverableDetailsConfig = (
    childCoverable: ChildAccidentDTO
): TableInformation => {
    return {
        title: childCoverable.name ? `Oplysninger om ${childCoverable.name}` : '',
        rowsInformation: [
            {
                name: 'Fulde navn',
                fieldValue: childCoverable.fullName,
                hidden: !childCoverable.fullName
            },
            {
                name: 'CPR-nummer',
                fieldValue: childCoverable.cprNumber,
                hidden: !childCoverable.cprNumber
            },
            {
                name: 'Født',
                fieldValue: toLongDateString(childCoverable.dateOfBirth),
                hidden: !!childCoverable.cprNumber || !childCoverable.dateOfBirth
            }
        ]
    };
};

const getTravelInformationCoverableDetailsConfig = (
    travelCoverable: TravelDTO
): TableInformation => {
    const isHouseholdFlavour = travelCoverable.flavourName === 'Rejseforsikring';
    const coverableName = isHouseholdFlavour
        ? travelCoverable.name
        : `${travelCoverable.insuredFullName ?? ''} - og evt. delebørn`;
    const modalTitleKey = isHouseholdFlavour
        ? 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-HOUSEHOLD-HEADER'
        : 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-INDIVIDUAL-HEADER';
    const modalDescriptionKey = isHouseholdFlavour
        ? 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-HOUSEHOLD-DESCRIPTION'
        : 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-INDIVIDUAL-DESCRIPTION';

    return {
        title: 'Hvem og hvad er omfattet?',
        rowsInformation: [
            {
                name: 'Personer, som er dækket',
                fieldValue: coverableName,
                valueType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: modalTitleKey,
                        description: modalDescriptionKey
                    }
                }
            },
            {
                name: 'Dækningsperiode',
                fieldValue: 'Alle årets ferier i op til 60 dage i træk'
            }
        ]
    };
};

const getYouthContentInformationCoverableDetailsConfig = (
    youthCoverable: YouthContentDTO
): TableInformation => {
    return {
        title: 'Oplysinger om Forsikringssted',
        rowsInformation: [
            {
                name: 'Forsikringssted',
                fieldValue: youthCoverable.insuredAddress
            }
        ]
    };
};

const getPrivateContentInformationCoverableDetailsConfig = (
    contentCoverable: PrivateContentDTO
): TableInformation => {
    return {
        title: 'Oplysinger om Forsikringssted',
        rowsInformation: [
            {
                name: 'Forsikringssted',
                fieldValue: contentCoverable.insuredAddress
            },
            {
                name: 'Antal beboere tilmeldt din folkeregisteradresse',
                fieldValue: contentCoverable.numberInHousehold?.toString(),
                hidden: contentCoverable.hasCohousing
            },
            {
                name: 'Antal beboere med folkeregisteradresse i bofællesskabet',
                fieldValue: contentCoverable.numberInHousehold?.toString(),
                hidden: !contentCoverable.hasCohousing,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-TITLE',
                        description:
                            'DSS-CON-HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Medforsikret ægtefælle/samlever med anden folkeregisteradresse',
                fieldValue: contentCoverable.coinsuredName,
                hidden: !contentCoverable.coinsuredBirthday && !contentCoverable.coinsuredName
            },
            {
                name: 'Medforsikret ægtefælle/samlever er født',
                fieldValue: contentCoverable.coinsuredBirthday,
                hidden: !contentCoverable.coinsuredBirthday && !contentCoverable.coinsuredName
            },
            {
                name: 'Tagtype',
                fieldValue: contentCoverable.roofType
            }
        ]
    };
};

const getPrivateCarInformationCoverableDetailsConfig = (
    carCoverable: CarDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: getHippoText('DSS-COVERABLE-DETAILS-INFORMATION-ON-THE-CAR-HEADER'),
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-REGISTRATION-PLATE-TITLE'),
                fieldValue: carCoverable.registrationNumber
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FIRST- REGISTRATION-DATE-TITLE'),
                fieldValue: carCoverable.firstRegistrationDate
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAKE-AND-MODEL-TITLE'),
                fieldValue: `${carCoverable.make} ${carCoverable.model}`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VARIANT-TITLE'),
                fieldValue: carCoverable.variant
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-YEAR-TITLE'),
                fieldValue: carCoverable.year.toString()
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-ENGINE-SIZE-TITLE'),
                fieldValue: carCoverable.enginePower
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FUEL-TYPE-TITLE'),
                fieldValue: carCoverable.fuelType
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAXIMUM-POWER-TITLE'),
                fieldValue: `${carCoverable.power.toString()} kW`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VIN-NUMBER-TITLE'),
                fieldValue: carCoverable.vin
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-THE-CAR-IS-LEASED-TITLE'),
                fieldValue: carCoverable.isInLeasing
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-LEASING-PERIOD-TITLE'),
                fieldValue: `${toLongDateString(carCoverable.leasingStartDate)}
                - ${toLongDateString(carCoverable.leasingEndDate)}`,
                hidden: !carCoverable.isInLeasing_bool
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-THE-CAR-IS-USED-FOR-TITLE'),
                fieldValue: carCoverable.vehicleUse
            }
        ]
    };
};
